import React, { useEffect, useRef, useState } from 'react';
import { useTranslations } from 'hooks/useTranslations';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { StyledPageTitle } from 'theme/styles';
import { TransactionSearchForm } from 'pages/TransactionsPage/TransactionSearchForm';
import { ISearchFormValues } from 'models/transaction';
import { useTransactions } from 'pages/TransactionsPage/hooks';
import { EPerPages, ISortOptions, ITableDataSource } from 'models/table';
import { Loader } from 'components/atoms/Loader';
import { Table } from 'components/molecules/Table';
import {
  mapTransactionsToDataSource,
  transactionsTableColumns,
} from 'pages/TransactionsPage/helpers';
import { Pagination } from 'components/molecules/Pagination';
import { areDatesEqual, formatDateToRequest } from 'utils/date';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { CardList } from 'components/molecules/CardList';
import { TransactionItemCard } from 'pages/TransactionsPage/TransactionItemCard';
import { ERouteLinks } from 'models/route';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Grid, Tooltip } from '@mui/material';
import { useDictionary } from 'models/dictionaryContext';
import { useLang } from 'models/langContext';
import {
  renderDataColor,
  renderRowColor,
  renderRowColorHover,
} from 'components/molecules/Table/helpers';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { objectToQueryString, prepareQueryValues } from 'helpers/searchQuery';
import { isExportNotAvailable } from 'constants/Export';
import { usePermission } from 'hooks/usePermission';
import { EPermission } from 'models/permissions';
import { TableTopButtons } from 'components/atoms/TableTopButtons';
import Button from 'components/atoms/Button';
import { EButtonVariants } from 'constants/Buttons';
import { exportTransactionsCSV } from 'requests/transaction';
import { joinValuesToString } from 'helpers/joinValuesToString';
import { areArraysEqual } from 'utils/arrays';
import { replaceUrlIfOnPage } from 'utils/history';

export interface ISearchParams {
  [key: string]: string | number | string[];
}

const initialFormValues: ISearchFormValues = {
  preset: '',
  transactionTypesUniqueIds: [],
  transactionTimeFrom: '',
  transactionTimeTo: '',
  createTimeFrom: '',
  createTimeTo: '',
  legalityUniqueId: '',
  expirationTimeFrom: '',
  expirationTimeTo: '',
  ownerUniqueId: '',
  keeperUniqueId: '',
  ownerName: '',
  keeperName: '',
  asset: undefined,
};

const TransactionsPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslations();
  const { getLegality, getActivity, getManufacturer, manufacturer } = useDictionary();
  const { selectedLanguage } = useLang();
  const { isDesktop } = useThemeBreakpoint();
  const { hasPermissions } = usePermission();
  const { pathname }: any = useLocation();
  const { getTransactionsData, transactions, paginator } = useTransactions();
  const [queryTransactions, setQueryTransactions] = useState<Partial<ISearchFormValues>>({});
  const [isQueryTransactionsLoading, setIsQueryTransactionsLoading] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const perPageFromQuery = Number.parseInt(searchParams.get('perPage') || '25', 10);
  const currentPageFromQuery = Number.parseInt(searchParams.get('currentPage') || '1', 10);
  const [currentPage, setCurrentPage] = useState<number>(
    currentPageFromQuery > 0 ? currentPageFromQuery : 1,
  );
  const [perPage, setPerPage] = useState<EPerPages>(
    [25, 50, 100].includes(perPageFromQuery)
      ? (perPageFromQuery as EPerPages)
      : EPerPages.perPage25,
  );
  const [sort, setSort] = useState<ISortOptions | null>(null);
  const [isTransactionsLoading, setIsTransactionsLoading] = useState<boolean>(true);
  const [isSortLoaded, setIsSortLoaded] = useState<boolean>(true);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isDataLoaded, setIsDataLoaded] = useState<number>(0);
  const [initialValues, setInitialValues] = useState<ISearchFormValues>(initialFormValues);
  const [isSearchParams, setIsSearchParams] = useState<boolean>(false);
  const [isPaginationLoading, setIsPaginationLoading] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isExportLoading, setIsExportLoading] = useState(false);

  const { midDateFormat, getProperties } = useGlobalProperty();

  const scrollPosition = useRef(0);

  useEffect(() => {
    getProperties();
  }, []);

  const previousSortRef = useRef(sort);

  useEffect(() => {
    (async function getTransactionDataInit() {
      if (Object.keys(queryTransactions).length > 0) {
        if (!transactions.length) {
          setIsTransactionsLoading(true);
        }

        if (sort !== previousSortRef.current) {
          setIsSortLoaded(false);
        }
        setIsPaginationLoading(true);
        await getTransactionsData(currentPage, perPage, queryTransactions, sort);
        setIsTransactionsLoading(false);
        setIsQueryTransactionsLoading(false);
        setIsPaginationLoading(false);
        setIsSortLoaded(true);
        previousSortRef.current = sort;
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryTransactions, sort, selectedLanguage, previousSortRef]);

  useEffect(() => {
    if (isSortLoaded) {
      window.scrollTo({ top: scrollPosition.current, behavior: 'auto' });
    }
  }, [isSortLoaded]);

  useEffect(() => {
    (async function initDictionaries() {
      getLegality();
      getActivity();
      getManufacturer();
      setIsDataLoaded(isDataLoaded + 1);
    })();
  }, [selectedLanguage]);

  useEffect(() => {
    const query = new URLSearchParams(searchParams);
    const queryValues = prepareQueryValues(
      {
        ...initialFormValues,
        perPage,
        currentPage,
      },
      query,
      [
        'transactionTimeFrom',
        'transactionTimeTo',
        'createTimeFrom',
        'createTimeTo',
        'expirationTimeFrom',
        'expirationTimeTo',
      ],
    );

    if (queryValues && Object.keys(queryValues).length) {
      setIsSearchParams(true);
    }
    setInitialValues({ ...initialFormValues, ...queryValues });
  }, [searchParams]);

  const getNewQueryPath = (values: Partial<ISearchFormValues>) => {
    const valuesWithoutPreset = { ...values };
    valuesWithoutPreset.preset = undefined;

    const ownerUniqueId = values.ownerName?.trim() === '' ? '' : (values.ownerUniqueId ?? '');
    const keeperUniqueId =
      values.keeperName?.trim() === '' ? '' : (values.keeperUniqueId ?? '');
    const transactionTimeFrom = formatDateToRequest(values.transactionTimeFrom, true, true);
    const transactionTimeTo = formatDateToRequest(values.transactionTimeTo, true, true);
    const createTimeFrom = formatDateToRequest(values.createTimeFrom, true, true);
    const createTimeTo = formatDateToRequest(values.createTimeTo, true, true);
    const expirationTimeFrom = formatDateToRequest(values.expirationTimeFrom, true, true);
    const expirationTimeTo = formatDateToRequest(values.expirationTimeTo, true, true);
    const transactionTypesUniqueIds =
      joinValuesToString(values.transactionTypesUniqueIds) ?? '';
    const queryTransactionsvalues: ISearchParams = {
      ...valuesWithoutPreset,
      transactionTimeFrom,
      transactionTimeTo,
      createTimeFrom,
      createTimeTo,
      expirationTimeFrom,
      expirationTimeTo,
      currentPage,
      perPage,
      ownerUniqueId,
      keeperUniqueId,
      transactionTypesUniqueIds,
    };
    const queryString = objectToQueryString(queryTransactionsvalues);
    if (queryString) {
      return `${pathname}?${queryString}`;
    }
    return pathname;
  };

  const handleSetQueryTransaction = (values: Partial<ISearchFormValues>) => {
    const valuesWithoutInvokedByPageChange = { ...values };
    valuesWithoutInvokedByPageChange.invokedByPageChange = undefined;

    replaceUrlIfOnPage(getNewQueryPath(valuesWithoutInvokedByPageChange));
    if (!isInitialLoad) {
      if (
        !areArraysEqual(
          values.transactionTypesUniqueIds,
          queryTransactions.transactionTypesUniqueIds,
        ) ||
        !areDatesEqual(values.transactionTimeFrom, queryTransactions.transactionTimeFrom) ||
        !areDatesEqual(values.transactionTimeTo, queryTransactions.transactionTimeTo) ||
        !areDatesEqual(values.createTimeFrom, queryTransactions.createTimeFrom) ||
        !areDatesEqual(values.createTimeTo, queryTransactions.createTimeTo) ||
        values.legalityUniqueId !== queryTransactions.legalityUniqueId ||
        !areDatesEqual(values.expirationTimeFrom, queryTransactions.expirationTimeFrom) ||
        !areDatesEqual(values.expirationTimeTo, queryTransactions.expirationTimeTo) ||
        (values.asset !== queryTransactions.asset &&
          (queryTransactions.asset !== undefined ||
            // @ts-expect-error: required by update
            values.asset !== '')) ||
        values.keeperUniqueId !== queryTransactions.keeperUniqueId ||
        values.ownerUniqueId !== queryTransactions.ownerUniqueId
      ) {
        setCurrentPage(1);
      }
    } else {
      setIsInitialLoad(false);
    }

    if (values.invokedByPageChange !== true) {
      setIsQueryTransactionsLoading(true);
    }

    setQueryTransactions(values);
  };

  return (
    <>
      <Breadcrumbs items={[t('transactionsList.transactions.header')]} />
      <StyledPageTitle variant="h4" style={{ marginBottom: '16px' }}>
        {t('transactionsList.transactions.header')}
      </StyledPageTitle>
      {!!isDataLoaded && initialValues && (
        <TransactionSearchForm
          onSubmit={handleSetQueryTransaction}
          initialFormValues={initialValues}
          isSearchParams={isSearchParams}
          isSearchButtonDisabled={isTransactionsLoading || isQueryTransactionsLoading}
          getNewQueryPath={getNewQueryPath}
          perPage={perPage}
          currentPage={currentPage}
        />
      )}
      {hasPermissions(EPermission.EXPORT_TRANSACTIONS) && (
        <TableTopButtons>
          <Grid columns={10} sx={{ justifyContent: 'flex-end', textAlign: 'right' }}>
            <Grid size={{ xs: 10, sm: 12, lg: 2 }}>
              {!(isTransactionsLoading || isQueryTransactionsLoading) &&
                (isExportNotAvailable(paginator) ? (
                  <Tooltip title={t('dataExportRecordsLimitAmount.label')} arrow>
                    <span>
                      <Button
                        label={t('transactions.export.button')}
                        variant={EButtonVariants.outlined}
                        onClick={() => {
                          setIsExportLoading(true);
                          exportTransactionsCSV(queryTransactions, sort).finally(() => {
                            setIsExportLoading(false);
                          });
                        }}
                        isLoading={isExportLoading}
                        disabled
                      />
                    </span>
                  </Tooltip>
                ) : (
                  <Button
                    label={t('transactions.export.button')}
                    variant={EButtonVariants.outlined}
                    onClick={() => {
                      setIsExportLoading(true);
                      exportTransactionsCSV(queryTransactions, sort).finally(() => {
                        setIsExportLoading(false);
                      });
                    }}
                    isLoading={isExportLoading}
                  />
                ))}
            </Grid>
          </Grid>
        </TableTopButtons>
      )}
      {isTransactionsLoading || isQueryTransactionsLoading ? (
        <Loader isLoading={true} />
      ) : (
        <>
          {isDesktop && !!isDataLoaded ? (
            <Table
              columns={transactionsTableColumns({
                midDateFormat,
                renderStateFieldColor: (data: ITableDataSource) =>
                  renderDataColor(data).stateField,
                t,
              })}
              dataSource={mapTransactionsToDataSource(transactions, manufacturer)}
              specificRowColor={renderRowColor}
              specificRowColorHover={renderRowColorHover}
              onSort={(it) => {
                setSort(it);
                scrollPosition.current = window.scrollY;
              }}
              isSortLoaded={isSortLoaded}
              customPaddingIfNoData="30px"
            />
          ) : (
            <CardList
              items={mapTransactionsToDataSource(transactions, manufacturer)}
              render={(data, index) => (
                <TransactionItemCard
                  data={data}
                  key={index}
                  handleView={(id: string) =>
                    navigate(ERouteLinks.ViewTransaction.replace(':id', id))
                  }
                />
              )}
            />
          )}
          {paginator && (
            <Pagination
              count={paginator?.totalElements}
              perPage={perPage}
              onChangePage={(page) => {
                setCurrentPage(page);
              }}
              onChangePerPage={(value) => {
                setPerPage(value);
              }}
              current={currentPage}
              isLoading={isPaginationLoading}
              isVisible={isSortLoaded}
            />
          )}
        </>
      )}
    </>
  );
};

export default TransactionsPage;
